import { FC } from 'react'
import { match } from 'ts-pattern'

import {
  Button,
  GreyGrey60,
  Spinner,
  Flex,
  Link
} from '@sequencehq/core-components'
import {
  customLocales,
  format,
  formatDistanceToNow
} from '@sequencehq/utils/dates'
import { getPortalDomain } from '@sequencehq/utils'
import { getEnvironment } from 'lib/environment/environment'
import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import { usePublishedStatus, PublishedStatus, useSigners } from '../../hooks'
import {
  ButtonCopyPublishedLink,
  ButtonDownloadQuotePdf,
  ButtonViewSchedule
} from '../../buttons'
import { SignatureProgressCard } from './SignatureProgressCard'
import { ButtonNotifyRecipients } from '../../modals'
import { useSignedOutsideOfSequence } from '../../hooks/useSignedOutsideOfSequence'
import { useQuoteApproval } from 'Cube/domain/selectors/useQuoteApproval'
import { CancelApprovalModal } from 'modules/common/modals/quotes/CancelApproval.modal'

const DATE_FORMAT: string = 'd MMM yyyy, HH:mm'

/** "Published 2d ago", as the card captions read. */
const publishedCaption = (publishedAt: Date | undefined) =>
  publishedAt
    ? `Published ${formatDistanceToNow(publishedAt, {
        addSuffix: true,
        locale: customLocales.enGb
      })}`
    : undefined

export const SignatureProgress: FC = () => {
  const ctx = useCubeContext()
  const publishedStatus = usePublishedStatus()
  const { completedSigners, allSigners } = useSigners()
  const signedOutsideOfSequence = useSignedOutsideOfSequence()
  const { approvalRequestedAt } = useQuoteApproval()

  return match(publishedStatus)
    .with(PublishedStatus.WaitingForApproval, () => (
      <SignatureProgressCard
        data-testid="quote.signatureProgress.waitingForApproval"
        title="Waiting for review..."
        caption={
          approvalRequestedAt
            ? `Review requested on ${format(approvalRequestedAt, DATE_FORMAT)}`
            : undefined
        }
        stepsCompleted={0}
        isActive
      >
        <CancelApprovalModal
          trigger={
            <Button
              variant="secondary"
              style={{ flex: '1' }}
              data-testid="quote.signatureProgress.cancelApproval"
            >
              Cancel request
            </Button>
          }
        />
      </SignatureProgressCard>
    ))
    .with(PublishedStatus.Published, () => {
      const publishedAt = ctx.queries.rawData.data.quote.publishedAt

      return (
        <SignatureProgressCard
          data-testid="quote.signatureProgress.published"
          title="Published"
          caption={publishedCaption(publishedAt)}
          stepsCompleted={1}
          isActive={false}
        >
          <ButtonNotifyRecipients {...NOTIFY_RECIPIENTS_PROPS} />
          <ButtonCopyPublishedLink data-testid="quote.signatureProgress.copyPublishedLink" />
          <ButtonDownloadQuotePdf data-testid="quote.signatureProgress.downloadQuotePdf" />
        </SignatureProgressCard>
      )
    })
    .with(PublishedStatus.PreparingSignatureRequest, () => {
      return (
        <SignatureProgressCard
          data-testid="quote.signatureProgress.preparingSignatureRequest"
          title="Preparing signature request..."
          caption={
            <Flex gap="6px" alignItems="center">
              <Spinner width="12px" height="12px" color={GreyGrey60} />
              <SignatureProgressCard.Caption data-testid="quote.signatureProgress.preparingSignatureRequest.caption">
                This might take a moment
              </SignatureProgressCard.Caption>
            </Flex>
          }
          stepsCompleted={1}
          isActive
        >
          <ButtonNotifyRecipients {...NOTIFY_RECIPIENTS_PROPS} disabled />
          <ButtonCopyPublishedLink data-testid="quote.signatureProgress.copyPublishedLink" />
          <ButtonDownloadQuotePdf data-testid="quote.signatureProgress.downloadQuotePdf" />
        </SignatureProgressCard>
      )
    })
    .with(PublishedStatus.ReadyToSign, () => {
      return (
        <SignatureProgressCard
          data-testid="quote.signatureProgress.readyToSign"
          title="Waiting for signatures"
          caption={publishedCaption(ctx.queries.rawData.data.quote.publishedAt)}
          secondaryCaption={`${completedSigners.length} of ${allSigners.length} signed`}
          stepsCompleted={1}
          isActive
        >
          <ButtonNotifyRecipients {...NOTIFY_RECIPIENTS_PROPS} />
          <ButtonCopyPublishedLink data-testid="quote.signatureProgress.copyPublishedLink" />
          <ButtonDownloadQuotePdf data-testid="quote.signatureProgress.downloadQuotePdf" />
        </SignatureProgressCard>
      )
    })
    .with(PublishedStatus.ProcessingSignatures, () => {
      return (
        <SignatureProgressCard
          data-testid="quote.signatureProgress.processingSignatures"
          title="Processing signatures..."
          stepsCompleted={1}
          isActive
        >
          <ButtonNotifyRecipients {...NOTIFY_RECIPIENTS_PROPS} disabled />
          <ButtonCopyPublishedLink data-testid="quote.signatureProgress.copyPublishedLink" />
          <ButtonDownloadQuotePdf data-testid="quote.signatureProgress.downloadQuotePdf" />
        </SignatureProgressCard>
      )
    })
    .with(PublishedStatus.Accepted, () => {
      const acceptedAt = ctx.queries.rawData.data.quote.acceptedAt

      return (
        <SignatureProgressCard
          data-testid="quote.signatureProgress.accepted"
          title={
            signedOutsideOfSequence
              ? 'Quote signed outside of Sequence'
              : 'Fully signed'
          }
          caption={
            acceptedAt
              ? `Completed ${format(acceptedAt, DATE_FORMAT)}`
              : undefined
          }
          stepsCompleted={2}
          isActive={false}
        >
          <ButtonViewSignedQuote
            data-testid="quote.signatureProgress.viewSignedQuote"
            disabled={signedOutsideOfSequence}
          />
          <ButtonCopyPublishedLink data-testid="quote.signatureProgress.copyPublishedLink" />
          <ButtonDownloadQuotePdf data-testid="quote.signatureProgress.downloadQuotePdf" />
        </SignatureProgressCard>
      )
    })
    .with(PublishedStatus.Executed, () => {
      const executedAt = ctx.queries.rawData.data.quote.executedAt

      return (
        <SignatureProgressCard
          data-testid="quote.signatureProgress.executed"
          title="Quote executed"
          caption={
            executedAt
              ? `Quote executed on ${format(executedAt, DATE_FORMAT)}`
              : undefined
          }
          stepsCompleted={3}
          isActive={false}
        >
          <ButtonViewSchedule
            style={{ flex: '1' }}
            data-testid="quote.signatureProgress.viewSchedule"
          />
          <ButtonCopyPublishedLink data-testid="quote.signatureProgress.copyPublishedLink" />
          <ButtonDownloadQuotePdf data-testid="quote.signatureProgress.downloadQuotePdf" />
        </SignatureProgressCard>
      )
    })
    .with(
      PublishedStatus.NotPublished,
      PublishedStatus.Template,
      PublishedStatus.Archived,
      () => null
    )
    .exhaustive()
}

/**
 * Notifying is how a published quote is shared, and ticking a signer in there
 * is what asks for signatures - so one button covers every card that has
 * somebody left to write to.
 */
const NOTIFY_RECIPIENTS_PROPS = {
  style: { flex: '1' },
  'data-testid': 'quote.signatureProgress.notifyRecipients'
} as const

const ButtonViewSignedQuote: FC<{
  'data-testid'?: string
  disabled?: boolean
}> = ({ 'data-testid': dataTestId, disabled }) => {
  const ctx = useCubeContext()
  const { customerId, id } = ctx.queries.rawData.data.common

  return (
    <Button
      variant="secondary"
      style={{
        flex: '1'
      }}
      {...(disabled
        ? {}
        : {
            as: Link.Unstyled,
            to: `${getPortalDomain(getEnvironment())}/quotes/${customerId}/${id}`,
            target: '_blank',
            rel: 'noopener noreferrer'
          })}
      data-testid={dataTestId}
      disabled={disabled}
    >
      View signed quote
    </Button>
  )
}
